import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {switchMap} from 'rxjs/operators';
import {AnalyticsService} from '../analytics.service';
import {ApiService, HttpError} from '../api.service';
import {AppStateService} from '../app-state.service';
import {IdentificationService} from '../identification.service';
import {ModalComponent} from '../modal/modal.component';
import {NameFormatPipe} from '../name-format.pipe';
import {PasswordsService} from '../passwords.service';

type IdentificationState = 'identify' | 'register' | 'login' | 'error' | 'verify_email' | 'email_sent' | null;

@Component({
  selector: 'identify-modal',
  templateUrl: './identify-modal.component.html',
  styleUrls: ['./identify-modal.component.scss']
})
export class IdentifyModalComponent implements OnInit, OnChanges {

  @Input() positionId: string | null = null;
  @Input() staff: StaffInfo[] | null = null;
  @Input() emailSent = false;
  @Input() dismissible = true;

  @Output() success = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  @ViewChild('modal') modal: ModalComponent;

  errorKey: string | null = null;
  errorCode: string | null = null;
  errorFatal = false;
  loading = false;
  staffInfo: string[] = [];
  state: IdentificationState = null;
  submitted = false;

  loginForm: FormGroup;
  registerForm: FormGroup;
  identifyForm: FormGroup;

  constructor(private Identification: IdentificationService,
              private Api: ApiService,
              private Analytics: AnalyticsService,
              private Passwords: PasswordsService,
              private Form: FormBuilder,
              private Change: ChangeDetectorRef,
              private AppState: AppStateService) {
  }

  ngOnInit() {
    this.loginForm = this.Form.group({
      password: ['', Validators.required]
    });

    this.registerForm = this.Form.group({
      newPassword: [''],
      confirmedPassword: ['']
    });

    this.identifyForm = this.Form.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['staff'] && this.staff) {
      this.staffInfo = this.staff.map(member => {
        let str = NameFormatPipe.apply(member, this.AppState.locale$.getValue()); // FIXME use observable
        if (member.phone && member.phone.trim()) {
          str += ' at ' + member.phone; // TODO translate
        }
        return str;
      });
    }
  }

  submitLogin() {
    this.submitted = true;
    this.errorKey = null;
    this.errorCode = null;
    if (this.loginForm.valid) {
      this.loading = true;
      this.Analytics.trackEvent('Identification log in submitted', {category: 'Action'});
      this.Api.authenticate(this.Identification.username, this.loginPassword.value)
        .pipe(switchMap(() => this.Api.refreshUser()))
        .subscribe(() => {
          this.success.next();
          this.modal.hide();
        }, (err: HttpError) => {
          if (err && (err.status === 400 || err.status === 401)) {
            this.errorKey = 'login.error.incorrect-password';
          } else {
            this.errorKey = 'error.request-failed';
            this.errorCode = err && err.code;
          }
          this.loading = false;
          this.Analytics.trackEvent('Identification log in failure');
          this.Change.detectChanges();
        });
    }
  }

  submitRegister() {
    this.submitted = true;
    this.errorKey = null;
    this.errorCode = null;
    if (this.registerForm.valid) {
      this.loading = true;
      this.Analytics.trackEvent('Registration submitted', {category: 'Action'});
      this.Api.initRegistration(this.Identification.email, this.registerPassword.value, this.positionId)
        .subscribe(() => {
            this.state = 'email_sent';
            this.loading = false;
            this.Change.detectChanges();
          },
          (err: HttpError) => {
            this.Analytics.trackEvent('Registration failed', {
              category: 'Error',
              email: this.Identification.email,
              registrationPasswordErrorCode: err && err.code
            });
            this.Identification.error = 'unknown';
            this.errorCode = err && err.code;
            this.checkState();
          });
    }
  }

  submitIdentify() {
    this.submitted = true;
    this.errorKey = null;
    this.errorCode = null;
    if (this.identifyForm.valid) {
      this.loading = true;
      this.Analytics.trackEvent('Identification email submitted', {category: 'Action'});
      this.Api.identifyWithOrder(this.identifyEmail.value, this.positionId)
        .subscribe(data => {
          this.Identification.handleResponse(this.identifyEmail.value, data);
          this.checkState();
        }, (err: HttpError) => {
          this.errorKey = 'error.request-failed';
          this.errorCode = err && err.code;
          this.loading = false;
          this.Change.detectChanges();
        });
    }
  }

  show() {
    this.checkState();
    this.modal.show();
    this.Change.detectChanges();
    this.Analytics.trackEvent('Identify modal: shown');
  }

  hide(): void {
    this.modal.hide();
    this.Analytics.trackEvent('Identify modal: closed');
  }

  onHide() {
    this.reset();
    this.close.next();
    this.Change.detectChanges();
  }

  get loginPassword() {
    return this.loginForm.get('password');
  }

  get registerPassword() {
    return this.registerForm.get('newPassword');
  }

  get registerPasswordConfirm() {
    return this.registerForm.get('confirmedPassword');
  }

  get identifyEmail() {
    return this.identifyForm.get('email');
  }

  private checkState(): void {
    const data = this.Identification;
    if (data.error === 'verify_email') {
      this.state = 'verify_email';
    } else if (data.error) {
      this.errorKey = this.getIdentificationErrorKey();
      this.errorFatal = !(data.error === 'account_inactive' || data.error === 'not_found');
      this.state = 'error';
    } else if (data.username) {
      this.state = 'login';
    } else if (!data.email) {
      this.identifyEmail.setValue(data.email);
      this.state = 'identify';
    } else {
      this.state = 'register';
    }
    this.submitted = false;
    this.loading = false;
    this.Change.detectChanges();
  }

  private getIdentificationErrorKey(): string {
    switch (this.Identification.error) {
      case 'account_inactive':
        return 'identify-modal.error.account-inactive';
      case 'not_found':
        return 'identify-modal.error.not-found';
      default:
        return 'identify-modal.error.unknown';
    }
  }

  private reset() {
    this.Identification.reset();
    this.errorKey = null;
    this.errorCode = null;
    this.errorFatal = false;
    this.loading = false;
    this.state = null;
    this.submitted = false;
    this.resetPasswords();
  }

  private resetPasswords() {
    this.registerPassword.setValue('');
    this.registerPasswordConfirm.setValue('');
    this.registerPassword.markAsUntouched();
    this.registerPasswordConfirm.markAsUntouched();
  }
}
